import React, {useCallback} from 'react';

import {usePanelSearchDebouncedContext} from '../../../state/panelbank/PanelSearchContextProvider';
import {maybePluralize} from '../../../util/uihelpers';
import {useQuickAddKeys} from '../../WorkspaceDrawer/PanelsQuickAdd/hooks/useQuickAddKeys';
import {usePanelsQuickAddContext} from '../../WorkspaceDrawer/PanelsQuickAdd/PanelsQuickAddContext';
import {useSearchResultCounts} from './useSearchResultCounts';

export const SearchResultCounts = () => {
  const {debouncedSearchQuery} = usePanelSearchDebouncedContext();

  // compute number of matching panels/sections
  const {numPanelsMatched, numSectionsMatched} = useSearchResultCounts();

  // compute number of matching keys that can be added as panels
  const {keysWithNoPanels} = useQuickAddKeys(debouncedSearchQuery);
  const numAvailableKeys = keysWithNoPanels.length;
  const {openQuickAdd} = usePanelsQuickAddContext();
  const openGlobalQuickAddWithSearch = useCallback(
    () =>
      openQuickAdd({
        location: 'Panel bank search',
        searchStr: debouncedSearchQuery,
      }),
    [debouncedSearchQuery, openQuickAdd]
  );

  // determine if counts should be displayed
  const hasPanelsOrSections = numPanelsMatched > 0 || numSectionsMatched > 0;
  const hasData = hasPanelsOrSections || numAvailableKeys > 0;
  if (!hasData) {
    return null;
  }

  return (
    <p className="text-moon-500">
      {hasPanelsOrSections && (
        <>
          {numPanelsMatched > 0 && (
            <span className="font-semibold">
              {maybePluralize(numPanelsMatched, 'panel', 's', true)}
            </span>
          )}
          {numPanelsMatched > 0 && numSectionsMatched > 0 && ' and '}
          {numSectionsMatched > 0 && (
            <span className="font-semibold">
              {maybePluralize(numSectionsMatched, 'section', 's', true)}
            </span>
          )}
          {' in this workspace'}
        </>
      )}
      {numAvailableKeys > 0 && (
        <>
          {hasPanelsOrSections && ', '}
          <span
            role="button"
            className="cursor-pointer font-semibold text-teal-600"
            onClick={openGlobalQuickAddWithSearch}>
            {maybePluralize(numAvailableKeys, 'available key', 's', true)}
          </span>
        </>
      )}
    </p>
  );
};
